import { InfoOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { Box, Typography, alpha, styled, useTheme } from '@mui/material';
import { FC, PropsWithChildren } from 'react';

const SectionWrapper = styled(Box)({
  marginBottom: 32
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1.5),
  paddingBottom: theme.spacing(0.75),
  borderBottom: `1px solid ${theme.palette.divider}`
}));

export const DocSection: FC<PropsWithChildren<{ title: string }>> = ({ title, children }) => (
  <SectionWrapper>
    <SectionTitle variant="h6">{title}</SectionTitle>
    {children}
  </SectionWrapper>
);

const CodePre = styled('pre')(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  overflowX: 'auto',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
  border: `1px solid ${theme.palette.divider}`,
  fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace',
  fontSize: '0.8rem',
  lineHeight: 1.6,
  whiteSpace: 'pre'
}));

export const DocCodeBlock: FC<PropsWithChildren<{ language?: string }>> = ({ children }) => (
  <CodePre>
    <code>{children}</code>
  </CodePre>
);

type CalloutType = 'info' | 'warning' | 'danger';

const calloutConfig: Record<CalloutType, { icon: React.ElementType; paletteKey: 'info' | 'warning' | 'error' }> = {
  info: { icon: InfoOutlined, paletteKey: 'info' },
  warning: { icon: WarningAmberOutlined, paletteKey: 'warning' },
  danger: { icon: WarningAmberOutlined, paletteKey: 'error' }
};

const CalloutWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$color'
})<{ $color: string }>(({ $color }) => ({
  display: 'flex',
  gap: 12,
  padding: 16,
  borderRadius: 4,
  border: `1px solid ${alpha($color, 0.4)}`,
  backgroundColor: alpha($color, 0.06),
  marginBottom: 16
}));

const CalloutText = styled(Typography)({
  lineHeight: 1.6
});

export const DocCallout: FC<PropsWithChildren<{ type?: CalloutType }>> = ({ type = 'info', children }) => {
  const theme = useTheme();
  const { icon: Icon, paletteKey } = calloutConfig[type];
  const color = theme.palette[paletteKey].main;

  return (
    <CalloutWrapper $color={color}>
      <Icon style={{ fontSize: '1.2rem', color, marginTop: 2, flexShrink: 0 }} />
      <CalloutText variant="body2">{children}</CalloutText>
    </CalloutWrapper>
  );
};
